'use client'

import React, { useMemo, useRef, useState } from 'react'

import {
  hasApartmentGalleryContent,
  type ApartmentGalleryViewId,
  type ApartmentGalleryViews,
} from '@/lib/apartments/apartmentGallery'
import { useMessages } from '@/lib/i18n/useMessages'
import { cn } from '@/utilities/ui'

import { getDefaultGalleryView, getEnabledGalleryViewTabIds } from './galleryViewTabs'

import { RoomGalleryFullscreenDialog } from './RoomGalleryFullscreenDialog'
import { RoomGalleryViewer, type RoomGalleryViewerCopy } from './RoomGalleryViewer'

type RoomGalleryProps = {
  carouselKey: string
  className?: string
  views: ApartmentGalleryViews
}

export function RoomGallery({ carouselKey, className, views }: RoomGalleryProps) {
  const messages = useMessages()
  const copy = messages.roomDetail.gallery
  const fullscreenTriggerRef = useRef<HTMLButtonElement>(null)
  const enabledTabs = useMemo(() => getEnabledGalleryViewTabIds(views), [views])
  const [activeView, setActiveView] = useState<ApartmentGalleryViewId>(() =>
    getDefaultGalleryView(views),
  )
  const [gallerySlideIndex, setGallerySlideIndex] = useState(0)
  const [floorPlanSlideIndex, setFloorPlanSlideIndex] = useState(0)
  const [slideIndexCarouselKey, setSlideIndexCarouselKey] = useState(carouselKey)
  const [fullscreenOpen, setFullscreenOpen] = useState(false)

  if (slideIndexCarouselKey !== carouselKey) {
    setSlideIndexCarouselKey(carouselKey)
    setGallerySlideIndex(0)
    setFloorPlanSlideIndex(0)
  }

  const viewerCopy = useMemo<RoomGalleryViewerCopy>(
    () => ({
      afterLabel: copy.afterLabel,
      beforeLabel: copy.beforeLabel,
      ariaLabel: copy.ariaLabel,
      closeFullscreen: copy.closeFullscreen,
      next: copy.next,
      openFullscreen: copy.openFullscreen,
      position: copy.position,
      previous: copy.previous,
      tabs: {
        gallery: copy.tabs.gallery,
        floorPlans: copy.tabs.floorPlans,
        view360: copy.tabs.view360,
      },
      tabsAriaLabel: copy.tabsAriaLabel,
      floorPlansAriaLabel: copy.floorPlansAriaLabel,
      floorPlansPanelAriaLabel: copy.floorPlansPanelAriaLabel,
      view360PanelAriaLabel: copy.view360PanelAriaLabel,
    }),
    [copy],
  )

  if (!hasApartmentGalleryContent(views)) {
    return null
  }

  return (
    <>
      <RoomGalleryViewer
        activeView={activeView}
        carouselKey={carouselKey}
        className={cn('size-full', className)}
        copy={viewerCopy}
        fullscreenButtonRef={fullscreenTriggerRef}
        gallerySlideIndex={gallerySlideIndex}
        floorPlanSlideIndex={floorPlanSlideIndex}
        mode="inline"
        onActiveViewChange={setActiveView}
        onGallerySlideIndexChange={setGallerySlideIndex}
        onFloorPlanSlideIndexChange={setFloorPlanSlideIndex}
        onRequestFullscreen={() => setFullscreenOpen(true)}
        enabledTabs={enabledTabs}
        views={views}
      />

      <RoomGalleryFullscreenDialog
        activeView={activeView}
        ariaLabel={copy.fullscreenAriaLabel}
        carouselKey={carouselKey}
        copy={viewerCopy}
        gallerySlideIndex={gallerySlideIndex}
        floorPlanSlideIndex={floorPlanSlideIndex}
        onActiveViewChange={setActiveView}
        onClose={() => setFullscreenOpen(false)}
        onGallerySlideIndexChange={setGallerySlideIndex}
        onFloorPlanSlideIndexChange={setFloorPlanSlideIndex}
        open={fullscreenOpen}
        restoreFocusRef={fullscreenTriggerRef}
        enabledTabs={enabledTabs}
        views={views}
      />
    </>
  )
}
